import { defineNuxtRouteMiddleware, navigateTo } from "nuxt/app";
import { RouteLocationNormalized, useRouter } from "vue-router";
import { FilterProcessingService } from "@app/routing/FilterProcessingService";
import { FilterType } from "@studyportals/omnisearch-interfaces";
import { useRollbar } from "@composables/useRollbar";
import { getPortalTypeFromEnvServer } from "@app/utils/get-portal-type-from-env.server";
import { getPortalPathFromType } from "@app/utils/portal-detector";
import { isCatchAllRoute } from "@app/utils/route-helpers";

const filterProcessingService = FilterProcessingService.getInstance();
const filterTypes = Object.values(FilterType) as string[];

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized) => {
	if(import.meta.client) return;
	// Capture router synchronously before any async operations
	const router = useRouter();
	const { logError } = useRollbar();

	try {
		if (isCatchAllRoute(to)) {
			const educationLevel = getPortalPathFromType(getPortalTypeFromEnvServer());
			if (educationLevel) {
				return navigateTo({ path: `/search/${educationLevel}`, query: to.query }, { redirectCode: 301 });
			}
		}

		const portalType = getPortalTypeFromEnvServer();
		let filters = await filterProcessingService.processRoute(to);
		filters = filterProcessingService.ensureImplicitFilters(filters, portalType);
		const route = filterProcessingService.processFilters(router, filters, to);

		const nonFilterQueryKeys = Object.keys(to.query || {}).filter(q => !filterTypes.includes(q) && q !== 'mh');
		for(const key of nonFilterQueryKeys) {
			if(route.query === undefined) {
				route.query = {};
			}

			route.query[key] = to.query[key] as string;
		}

		const routeParamKeys = Object.keys(route.params || {});
		const toParamKeys = Object.keys(to.params || {});
		const paramsMatch = routeParamKeys.length === toParamKeys.length && routeParamKeys.every(key => route.params[key] === to.params[key]);

		const routeQueryKeys = Object.keys(route.query || {});
		const toQueryKeys = Object.keys(to.query || {});
		const queryMatch = routeQueryKeys.length === toQueryKeys.length && routeQueryKeys.every(key => route.query[key] === to.query[key]);

		if(route.name === to.name && paramsMatch && queryMatch) return;

		return navigateTo(route);
	} catch (error) {
		logError('Failed to process route filters', error as Error);
	}
});
